import React, { useCallback, useEffect, useId, useState } from 'react'
import { Card } from '../../../../../../shared/card/card'
import { Notice } from '../../../../../../shared/sso-workspace/fleet-page'
import { ConfirmDialog } from '../../../../../../shared/sso-workspace/confirm-dialog'
import { isCode, serverMessage } from '../../../../../../shared/error/server-message'
import { ErrorReference } from '../../../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../../../shared/error/use-error-line'
import { useAuthContext } from '../../../../../../context/auth.context'
import { useRoleOrgApiKey, useRoleOrgGroup } from '../../../../../../role/hook/role-org'
import { useApiAccess } from '../../../api-keys/use-api-access'
import { clearGroupExternalID, getGroupExternalID, listPartnerSystemKeys, setGroupExternalID } from '../../../../../../shared/partner/partner-api'
import { externalIDProblem, systemKeyProblem } from '../../../../../../shared/partner/partner-words'

// A group's partner external id (Phase E E1a; plan ruling E-11, Task 11):
// an organization admin maps the group to (system key, external id), so the
// partner API names it by the partner's own id when it sets people's groups.
// The pair is unique among the organization's live groups (409
// group.external_id_taken), and an LMS connection's learner group can't
// have one (409 group.lms_learner_group); the server's message says which.
// The system key's suggestions are the organization's live keys' (GET
// /partner-system-keys); a mapping may name another.
//
// GROUP read shows it; GROUP update maps and clears it, never from an
// impersonated session (content refuses: 403 auth.impersonation_not_allowed),
// which reads it and says why. Before the database has group external ids
// (503 group.external_ids_unavailable) the section stays away.
//
// Only organizations with partner API access see it (Dave, #2 6196467670):
// staff's switch, organizations.feature_partner_api, read as the sidebar's
// "API keys" entry reads it (GET /api-access, use-api-access.js), so a
// customer without the partner API never sees a field nobody can use. The
// switch decides, not the keys: access off hides it even with live keys.
// That read needs API_KEY read (owners, admins and staff), so whoever
// lacks it, or while the switch is unread or its read failed, sees nothing,
// and the group's external id is never asked for.

export const READ_ONLY_IMPERSONATING_GROUP = "You're signed in as someone else, so this group's partner external id can be viewed but not changed. End the impersonation to make changes."

// What a clear or a change of system key says (plan ruling E-23): the
// memberships the partner API added stay in the group, but content released
// them in the same transaction (its `released` count), so they are the
// organization's now and the partner API never changes them again. No
// count, no sentence.
const releasedSentence = released => {
  const n = Number(released) || 0
  if (n <= 0) return ''
  return n === 1
    ? " 1 membership the partner API added stays in the group; it's the organization's now, and the partner API won't change it."
    : ` ${n} memberships the partner API added stay in the group; they're the organization's now, and the partner API won't change them.`
}
export const clearedText = released => `Cleared.${releasedSentence(released)}`
export const savedText = released => `Saved.${releasedSentence(released)}`

// Before a mapped group moves to another system key (content releases the
// partner's memberships then; a new external id under the same key keeps
// them): what will happen to the memberships.
export const remapBody = oldSystemKey =>
  `Memberships the partner API added under ${oldSystemKey} will stay in the group; they'll be the organization's, and the partner API won't change them.`

const LEDE = "Lets the partner API name this group by your partner system's own id when it sets people's groups. Each system key and external id names one group of this organization."

export const GroupExternalID = ({ groupID }) => {
  const ids = { systemKey: useId(), externalID: useId(), suggestions: useId() }
  const role = useRoleOrgGroup()
  const apiKeyRole = useRoleOrgApiKey()
  const { state } = useAuthContext()
  const impersonating = !!(state && state.jwtClaims && state.jwtClaims.impersonationEndsAt)
  const access = useApiAccess(!!role.accessRead && !!apiKeyRole.accessRead)
  const partnerAPI = !!(access && access.enabled === true)
  const canRead = !!role.accessRead && partnerAPI
  const canEdit = canRead && !!role.accessUpdate && !impersonating

  const [mapping, setMapping] = useState(undefined)
  const [hidden, setHidden] = useState(false)
  const [loadError, loadFailure, setLoadError] = useErrorLine()
  const [systemKeys, setSystemKeys] = useState([])
  const [systemKey, setSystemKey] = useState('')
  const [externalID, setExternalID] = useState('')
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(null)
  const [error, failure, setError] = useErrorLine()
  const [clearing, setClearing] = useState(false)
  const [remapping, setRemapping] = useState(null)
  const [clearError, clearFailure, setClearError] = useErrorLine()

  const show = useCallback(found => {
    const next = found || { group_id: groupID, system_key: null, external_id: null }
    setMapping(next)
    setSystemKey(next.system_key || '')
    setExternalID(next.external_id || '')
  }, [groupID])

  useEffect(() => {
    if (!canRead || !groupID) return undefined
    let live = true
    getGroupExternalID(groupID).then(
      found => { if (live) { setLoadError(null); show(found) } },
      err => {
        if (!live) return
        if (isCode(err, 'group.external_ids_unavailable')) return setHidden(true)
        setMapping(null)
        setLoadError(serverMessage(err, "This group's partner external id couldn't be loaded."), err)
      }
    )
    return () => { live = false }
  }, [canRead, groupID, show, setLoadError])

  // the suggestions, for someone who can map (a failure only loses them)
  useEffect(() => {
    if (!canEdit) return undefined
    let live = true
    listPartnerSystemKeys().then(list => { if (live) setSystemKeys(Array.isArray(list) ? list : []) }, () => {})
    return () => { live = false }
  }, [canEdit])

  const persist = async () => {
    setBusy(true)
    setError(null)
    try {
      const saved = await setGroupExternalID(groupID, { systemKey: systemKey.trim(), externalID: externalID.trim() })
      setBusy(false)
      show(saved)
      setDone(savedText(saved && saved.released))
    } catch (err) {
      setBusy(false)
      setError(serverMessage(err, "The external id couldn't be saved."), err)
    }
  }

  const save = e => {
    if (e) e.preventDefault()
    if (busy) return
    setDone(null)
    const problem = systemKeyProblem(systemKey, { required: true }) || externalIDProblem(externalID)
    if (problem) return setError(problem)
    // a mapped group moving to another system key releases the partner's
    // memberships (E-23): ask first
    const oldSystemKey = mapping && mapping.system_key
    if (oldSystemKey && oldSystemKey !== systemKey.trim()) {
      setError(null)
      return setRemapping({ from: oldSystemKey })
    }
    return persist()
  }

  const confirmRemap = () => {
    setRemapping(null)
    return persist()
  }

  const clear = async () => {
    if (busy) return
    setBusy(true)
    setClearError(null)
    try {
      const cleared = await clearGroupExternalID(groupID)
      setBusy(false)
      setClearing(false)
      setError(null)
      show(cleared && cleared.group_id ? cleared : null)
      setDone(clearedText(cleared && cleared.released))
    } catch (err) {
      setBusy(false)
      setClearError(serverMessage(err, "The external id couldn't be cleared."), err)
    }
  }

  if (!canRead || hidden || mapping === undefined) return null

  const mapped = !!(mapping && mapping.system_key && mapping.external_id)

  return (
    <Card title="Partner external id">
      <p className="sw-hint small mb-2">{LEDE}</p>
      {loadError && <Notice tone="bad" role="alert">{loadError}<ErrorReference error={loadFailure} /></Notice>}
      {impersonating && role.accessUpdate && <Notice tone="warn">{READ_ONLY_IMPERSONATING_GROUP}</Notice>}

      {mapping && !canEdit &&
        (mapped
          ? <dl className="sw-dl mb-0">
            <dt>System key</dt><dd className="sw-mono">{mapping.system_key}</dd>
            <dt>External id</dt><dd>{mapping.external_id}</dd>
          </dl>
          : <p className="sw-hint m-0">Not mapped.</p>)}

      {mapping && canEdit &&
        <form onSubmit={save} noValidate>
          <div className="mb-2">
            <label className="sw-label" htmlFor={ids.systemKey}>System key</label>
            <input id={ids.systemKey} className="form-control sw-mono" type="text" list={ids.suggestions} value={systemKey} placeholder="acme-lms"
              autoComplete="off" autoCapitalize="none" spellCheck={false} onChange={e => { setDone(null); setSystemKey(e.target.value) }} />
            <datalist id={ids.suggestions}>
              {systemKeys.map(key => <option key={key} value={key} />)}
            </datalist>
          </div>
          <div className="mb-2">
            <label className="sw-label" htmlFor={ids.externalID}>External id</label>
            <input id={ids.externalID} className="form-control" type="text" value={externalID} autoComplete="off" spellCheck={false}
              onChange={e => { setDone(null); setExternalID(e.target.value) }} />
            <p className="sw-hint small mt-1 mb-0">The id your partner system uses for this group, exactly as it sends it: up to 255 characters.</p>
          </div>
          {error && <p className="text-danger small mb-1" role="alert">{error}</p>}
          {error && <ErrorReference error={failure} className="mb-2" />}
          {done && <p className="small text-success mb-1" role="status">{done}</p>}
          <div className="d-flex gap-2">
            <button type="submit" className="btn sw-btn-primary btn-sm" disabled={busy}>Save</button>
            {mapped && <button type="button" className="btn sw-btn btn-sm" disabled={busy} onClick={() => { setClearError(null); setClearing(true) }}>Clear</button>}
          </div>
        </form>}

      <ConfirmDialog
        open={Boolean(remapping)}
        title="Change this group's system key?"
        body={remapping ? remapBody(remapping.from) : ''}
        confirmText="Change system key"
        confirmClassName="btn sw-btn-primary"
        busy={busy}
        onConfirm={confirmRemap}
        onCancel={() => setRemapping(null)} />

      <ConfirmDialog
        open={clearing}
        title="Clear this group's partner external id?"
        body="The partner API can't name this group any more. Memberships it added here will stay in the group; they'll be the organization's, and the partner API won't change them. Memberships people added stay too."
        confirmText="Clear external id"
        busy={busy}
        error={clearError}
        failure={clearFailure}
        onConfirm={clear}
        onCancel={() => setClearing(false)} />
    </Card>
  )
}
